/**
 * mailability — may this property be sent physical mail?
 *
 * ⚠️ ONE FILE, SHARED BY SERVER AND BROWSER. `functions/models` is a symlink to
 * `src/models`, so addPropertiesToMailshot `require()`s this exact file (Node 20
 * loads ES modules through require) and the add-to-mailshot modal imports it.
 * An earlier version kept two copies plus a drift-guard test, reasoning that CRA
 * cannot import from outside src/ — true, but the answer was to put the rule
 * INSIDE src/models, where both sides already reach. Mailing policy that can
 * disagree with itself is exactly what this must never do.
 *
 * ⚠️ WHY A FUNCTION AND NOT A STORED `mailable` FLAG. The scraper session offered
 * to write `mailable: true|false` on every record, and agreed to drop it: grade
 * and evidence are FACTS about a match; "mailable" is POLICY about those facts,
 * and it belongs to Lee. Stamp policy into records and every rule change needs a
 * re-flag script someone must remember to run — forget once and the data lies.
 *
 * The rule (agreed with Lee, 10 Sep 2026):
 *   shadowRun                → NEVER. Test output from the shadow phase.
 *   no matchConfidence       → mailable. Predates confidence scoring entirely —
 *                              existing behaviour, deliberately unchanged.
 *   v1 engine, high          → mailable IF Rightmove supplied the address
 *                              (_matchingAudit.soldPropertyH1Address), i.e. it
 *                              predates the Sep 2026 API stripping. Otherwise
 *                              NOT (v1-high-unverified). On the scraper session's
 *                              evidence; on 10 Sep all 33,913 qualified.
 *   v1 engine, medium        → NEVER. ⚠️ A TIGHTENING: before this, ticking
 *                              "include medium" mailed these too.
 *   v2 engine, high + strong → mailable. THE DEFAULT for new scrapes.
 *   v2 engine, high + single → only with includeSingleEvidence (under evaluation)
 *   v2 engine, medium        → only with includeMediumConfidence. A `veto`
 *                              record is graded medium by v2, so it lands here.
 *
 * A v2 record is one v2 wrote — `resolvedFrom === "rightmove_house_prices"` (the
 * scraper confirms this is unmistakable), or carrying the `evidence` field only
 * v2 writes. A v2 record with unknown evidence is treated as NOT strong: fail
 * closed on a letter.
 *
 * ⚠️ NOT RE-GRADING THE OLD RECORDS (Lee, 10 Sep 2026). The scraper offered a v2
 * replay of all 33,913 v1-high records — ~85k proxied requests to Rightmove and
 * Land Registry. Declined: new mailings come from new scrapes, re-mailing old
 * addresses is not planned, and it risks rate-limiting or blocking on the APIs
 * the live scrapes depend on. REVISIT if re-mailing previous addresses is ever
 * planned.
 */

export const V2_SOURCE = 'rightmove_house_prices';

export function isV2Record(p) {
  return p.resolvedFrom === V2_SOURCE || (p.evidence != null && p.evidence !== '');
}

/**
 * @param {Object} p                 a discovery_properties document
 * @param {Object} [opts]
 * @param {boolean} [opts.includeSingleEvidence=false]
 * @param {boolean} [opts.includeMediumConfidence=false]
 * @returns {{mailable: boolean, reason: string}}
 */
export function mailabilityOf(p, opts = {}) {
  const { includeSingleEvidence = false, includeMediumConfidence = false } = opts;
  if (!p) return { mailable: false, reason: 'missing' };
  if (p.shadowRun === true) return { mailable: false, reason: 'shadow' };

  const conf = p.matchConfidence;
  if (!conf) return { mailable: true, reason: 'no-confidence' };

  if (!isV2Record(p)) {
    // ⚠️ v1 HIGH is mailable only when Rightmove itself supplied the address
    // (`soldPropertyH1Address`) and Land Registry then confirmed number and
    // street — i.e. it predates Rightmove stripping addresses from its API in
    // Sep 2026. That is the REASON v1 high is trustworthy, so the rule checks it
    // rather than leaning on a coincidence. On 10 Sep 2026 all 33,913 non-shadow
    // v1-high records carried it; the only ones that did not were shadow records
    // (the weaker one-sale kind), already excluded above. Fails closed.
    if (conf === 'high') {
      const audit = p._matchingAudit;
      if (audit && audit.soldPropertyH1Address) return { mailable: true, reason: 'v1-high' };
      return { mailable: false, reason: 'v1-high-unverified' };
    }
    return { mailable: false, reason: 'v1-medium' };
  }

  if (conf === 'high' && p.evidence === 'strong') {
    return { mailable: true, reason: 'v2-high-strong' };
  }
  if (conf === 'high') {
    return { mailable: !!includeSingleEvidence, reason: 'v2-high-single' };
  }
  return { mailable: !!includeMediumConfidence, reason: 'v2-medium' };
}

/**
 * Whether a REASON is mailable under the given switches.
 *
 * The reason is a fact about a record; whether that reason gets a letter is
 * policy. Per-scrape summaries store counts BY REASON and apply this at read
 * time, so flipping a switch re-counts instantly and nothing stored goes stale
 * when the rule changes — the same "policy is a function" decision as above.
 * Kept equivalent to mailabilityOf by a test over every vector and switch.
 */
export function isReasonMailable(reason, opts = {}) {
  const { includeSingleEvidence = false, includeMediumConfidence = false } = opts;
  switch (reason) {
    case 'no-confidence':
    case 'v1-high':
    case 'v2-high-strong':
      return true;
    case 'v2-high-single':
      return !!includeSingleEvidence;
    case 'v2-medium':
      return !!includeMediumConfidence;
    default:
      // shadow, v1-medium, v1-high-unverified, missing — and anything unknown,
      // so a reason added later fails closed until it is placed here.
      return false;
  }
}

export const SSTC_STATUS = 'Sold STC';
// A third listing status from 28 Sep 2026: the scraper only recognised Rightmove's
// SOLD marker, so Under Offer listings were saved as "For Sale". Kept SEPARATE from
// Sold STC (Lee) — whether it is mailed is a choice, not an assumption.
export const UNDER_OFFER_STATUS = 'Under Offer';

/**
 * Count records by mailing reason, and how many of each are Sold STC / Under
 * Offer. Facts only — no switches — so the result can be stored. Summaries
 * written before 28 Sep carry no `underOffer`, which is accurate: no record
 * said Under Offer until then.
 */
export function summariseByReason(properties) {
  const byReason = {};
  let total = 0;
  let sstc = 0;
  let underOffer = 0;
  for (const p of properties || []) {
    const { reason } = mailabilityOf(p);
    const bucket = byReason[reason] || (byReason[reason] = { total: 0, sstc: 0, underOffer: 0 });
    bucket.total += 1;
    total += 1;
    if (p && p.propertyStatus === SSTC_STATUS) {
      bucket.sstc += 1;
      sstc += 1;
    } else if (p && p.propertyStatus === UNDER_OFFER_STATUS) {
      bucket.underOffer += 1;
      underOffer += 1;
    }
  }
  return { byReason, total, sstc, underOffer };
}

/** Mailable totals from a stored summary, under the given switches. */
export function mailableFromSummary(summary, opts = {}) {
  const out = { total: 0, sstc: 0, underOffer: 0 };
  for (const [reason, c] of Object.entries((summary && summary.byReason) || {})) {
    if (isReasonMailable(reason, opts)) {
      out.total += c.total || 0;
      out.sstc += c.sstc || 0;
      out.underOffer += c.underOffer || 0;
    }
  }
  return out;
}

/** Partition a list, returning the keepers and a count per exclusion reason. */
export function partitionByMailability(properties, opts = {}) {
  const keep = [];
  const excluded = {};
  for (const p of properties) {
    const { mailable, reason } = mailabilityOf(p, opts);
    if (mailable) keep.push(p);
    else excluded[reason] = (excluded[reason] || 0) + 1;
  }
  return { keep, excluded };
}

/** Readable labels for why a property was held back — used by the modal. */
export const HELD_BACK_LABELS = {
  'v1-medium': 'older medium-confidence matches (never mailed)',
  'v1-high-unverified': 'older high-confidence matches without a Rightmove-supplied address',
  'v2-high-single': 'high confidence, single-sale evidence',
  'v2-medium': 'medium confidence',
  shadow: 'test records from the address-engine trial',
  missing: 'properties that could not be found',
};

// ── Describing a match in plain English ───────────────────────────────────────
//
// ⚠️ ENGINE-AWARE, because the same words mean different things. The old UI
// explained every grade in v1's terms ("high = PAON/SAON + street matched").
// Since the flip, a v2 record's "high" means Land Registry confirmed ONE unique
// house for the matched sales — so a v2 property was being given v1's
// explanation. The table dot, the badge and the detail view all read this, so
// they cannot tell a reviewer three different stories.

const CORROBORATION_TEXT = {
  lr_agree: 'Land Registry agrees with the address',
  lr_partial_street: 'Land Registry agrees on the street only',
  lr_partial_name: 'Land Registry agrees on the house name only',
  lr_partial_flat: 'Land Registry has the building, but a different flat',
  lr_absent: 'not found in Land Registry',
  lr_error: 'the Land Registry check failed',
};

const EVIDENCE_TEXT = {
  strong: 'two or more of its sales matched this house',
  single: 'only one of its sales matched this house',
};

/**
 * @returns {{engine, grade, evidence, headline, lines: string[], veto, mailable, mailReason}}
 */
export function describeMatch(p) {
  const out = {
    engine: null, grade: p?.matchConfidence || null, evidence: p?.evidence || null,
    headline: 'No confidence grade', lines: [], veto: p?.veto || null,
    ...(() => { const m = mailabilityOf(p); return { mailable: m.mailable, mailReason: m.reason }; })(),
  };
  if (!p) return out;
  if (p.shadowRun === true) {
    out.lines.push('A test record from the address-engine trial — never mailed.');
  }
  if (!out.grade) {
    out.lines.push('Saved before addresses were graded.');
    return out;
  }
  const Grade = out.grade === 'high' ? 'High' : 'Medium';

  if (isV2Record(p)) {
    out.engine = 'v2';
    out.headline = out.evidence ? `${Grade} confidence · ${out.evidence} evidence` : `${Grade} confidence`;
    out.lines.push(out.grade === 'high'
      ? 'Land Registry confirms one unique house for the matched sales, and the address agrees.'
      : 'Land Registry only partly confirms the match.');
    if (EVIDENCE_TEXT[out.evidence]) out.lines.push(`Evidence: ${EVIDENCE_TEXT[out.evidence]}.`);
    if (CORROBORATION_TEXT[p.corroboration]) out.lines.push(`Check: ${CORROBORATION_TEXT[p.corroboration]}.`);
    if (out.veto) out.lines.push(`Graded medium despite Land Registry: ${out.veto}.`);
  } else {
    out.engine = 'v1';
    out.headline = `${Grade} confidence · older engine`;
    out.lines.push(out.grade === 'high'
      ? (p._matchingAudit?.soldPropertyH1Address
          ? 'Rightmove supplied the address and Land Registry confirmed the number and street.'
          : 'Matched by the older engine, without a Rightmove-supplied address.')
      : 'Street-only match by the older engine — not mailed.');
  }
  return out;
}
